// For non multi-currency currencies, it needs to ISO 4217 so it works well with Intl.NumberFormat in the Frontend.

// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat#currency_2
export enum Currency {
  USD = 'USD', // US dollars
  EUR = 'EUR', // Euros
  GBP = 'GBP', // British pounds
  AUD = 'AUD', // Aussie dollars
  // Some data providers like PayPal has multi-currency accounts where
  // currency on account is declared as 'XXX' but transactions and
  // account balances do have valid currencies USD/EUR/...
  MULTI_CURRENCY = 'MULTI_CURRENCY',
}

export const CurrencyDetails = {
  [Currency.EUR]: {
    decimalPlaces: 2,
  },
  [Currency.USD]: {
    decimalPlaces: 2,
  },
  [Currency.GBP]: {
    decimalPlaces: 2,
  },
  [Currency.AUD]: {
    decimalPlaces: 2,
  },
  [Currency.MULTI_CURRENCY]: {
    decimalPlaces: 2,
  },
};

export enum SeapointAccountCurrency {
  GBP = 'GBP',
  EUR = 'EUR',
}

export enum LegalEntityCountry {
  GB = 'GB',
  IE = 'IE',
  DE = 'DE',
}

export enum TermsAndConditionsCountry {
  EU = 'EU',
  GB = 'GB',
}

export enum AgreedTermsAndConditionsFrom {
  COMPANY_CREATION = 'COMPANY_CREATION',
  TREASURY = 'TREASURY',
}

export enum CompanyRequestType {
  NON_GB_TREASURY = 'NON_GB_TREASURY',
}

/**
 * Enum for Account
 */
export enum AccountType {
  BANKING = 'BANKING',
  INVESTMENT = 'INVESTMENT',
}

export enum AccountStatus {
  ACTIVE = 'ACTIVE', // Account is fully operational
  CLOSED = 'CLOSED', // Account has been permanently closed
  INACTIVE = 'INACTIVE', // Account is temporarily suspended
  PENDING = 'PENDING', // Account is awaiting activation
}

/**
 * WealthKernel bank account status enum
 * @see https://wealthkernel.stoplight.io/docs/guides/f56c2d090d0c3-bank-account-basics
 */
export enum WealthKernelBankAccountStatus {
  PENDING = 'Pending',
  ACTIVE = 'Active',
  SUSPENDED = 'Suspended',
  INACTIVE = 'Inactive',
}

/**
 * WealthKernel investment account status enum
 * @see https://wealthkernel.stoplight.io/docs/api/2021-05-17/operations/list-accounts
 */
export enum WealthKernelInvestmentAccountStatus {
  PENDING = 'Pending',
  ACTIVE = 'Active',
  SUSPENDED = 'Suspended',
  CLOSING = 'Closing',
  CLOSED = 'Closed',
}

export enum WealthKernelInvestmentPortfolioStatus {
  CREATED = 'Created',
  ACTIVE = 'Active',
  CLOSING = 'Closing',
  CLOSED = 'Closed',
}

export enum WealthKernelInvestmentDepositStatus {
  CREATED = 'Created',
  SETTLED = 'Settled',
  CANCELLING = 'Cancelling',
  CANCELLED = 'Cancelled',
  REJECTED = 'Rejected',
}

export enum WealthKernelOrderStatus {
  PENDING = 'Pending',
  OPEN = 'Open',
  MATCHED = 'Matched',
  REJECTED = 'Rejected',
  CANCELLING = 'Cancelling',
  CANCELLED = 'Cancelled',
}

export enum WealthKernelOrderSide {
  BUY = 'Buy',
  SELL = 'Sell',
}

export enum WealthKernelFillStatus {
  MATCHED = 'Matched',
  CANCELLED = 'Cancelled',
}

export enum AccountState {
  LINKING = 'LINKING',
  LINKED = 'LINKED',
  UNLINKED = 'UNLINKED',
  SYNCING = 'SYNCING',
  SYNCED = 'SYNCED',
  SYNC_ERROR = 'SYNC_ERROR',
}

/**
 * Enum for Participant Types
 */
export enum ParticipantType {
  COMPANY = 'COMPANY', // Company/business entity
  VENDOR = 'VENDOR', // Vendor/supplier providing goods or services
  BENEFICIARY = 'BENEFICIARY', // Entity sending or receiving payment
  EMPLOYEE = 'EMPLOYEE', // Employee receiving payroll or expense reimbursement
  TREASURY = 'TREASURY', // Treasury participant
  SEAPOINT = 'SEAPOINT', // Our own participant, used for fees and charges that we take from our customers
}

export enum Workflow {
  VENDOR_ENRICHMENT = 'VENDOR_ENRICHMENT',
  TRANSACTION_SYNC = 'TRANSACTION_SYNC',
  BENEFICIARY_EXTRACTION = 'BENEFICIARY_EXTRACTION',
  DUPLICATE_VENDOR_MERGE = 'DUPLICATE_VENDOR_MERGE',
  TRANSACTION_VENDOR_JUDGE = 'TRANSACTION_VENDOR_JUDGE',
  INVOICE_JUDGE = 'INVOICE_JUDGE',
  INVOICE_IS_PAID = 'INVOICE_IS_PAID',
  EXTRACT_GMAIL_INVOICES = 'EXTRACT_GMAIL_INVOICES',
  ACCOUNT_UNLINK = 'ACCOUNT_UNLINK',
  INVOICE_REMATCH = 'INVOICE_REMATCH',
  AI_EVAL = 'AI_EVAL',
  DUPLICATE_TRANSACTION_REMOVAL = 'DUPLICATE_TRANSACTION_REMOVAL',
  XERO_ACCOUNT_IMPORT = 'XERO_ACCOUNT_IMPORT',
}

export enum WorkflowTriggerSource {
  CRON_JOB = 'CRON_JOB',
  WEBHOOK = 'WEBHOOK',
  USER_MANUAL_TRIGGER = 'USER_MANUAL_TRIGGER',
  ADMIN_MANUAL_TRIGGER = 'ADMIN_MANUAL_TRIGGER',
  OPEN_BANKING_CONNECTION = 'OPEN_BANKING_CONNECTION',
  SYSTEM_AUTOMATED_TRIGGER = 'SYSTEM_AUTOMATED_TRIGGER',
  XERO_TRANSACTION_IMPORT = 'XERO_TRANSACTION_IMPORT',
}

export enum AccountSyncEvents {
  LINK_START = 'LINK_START',
  LINK_END = 'LINK_END',
  SYNC_START = 'SYNC_START',
  SYNC_END = 'SYNC_END',
  LINK_ERROR = 'LINK_ERROR',
  SYNC_ERROR = 'SYNC_ERROR',
  UNLINK = 'UNLINK',
}

export enum BeneficiaryCreatedByType {
  USER = 'USER',
  SYSTEM = 'SYSTEM',
}

export enum BeneficiaryDeletedByType {
  USER = 'USER',
  ADMIN = 'ADMIN',
}

/** Beneficiary enums */
export enum BeneficiaryType {
  SCAN = 'SCAN',
  IBAN = 'IBAN',
}

export enum BeneficiaryAccountType {
  PERSONAL = 'PERSONAL',
  BUSINESS = 'BUSINESS',
}

/**
 * Eligible countries for beneficiaries EU SEPA Countries + United Kingdom
 * TODO: Validate the enum with Modulr
 */
export enum BeneficiaryCountry {
  IE = 'IE',
  GB = 'GB',
  AT = 'AT',
  BE = 'BE',
  BG = 'BG',
  HR = 'HR',
  CH = 'CH',
  CY = 'CY',
  CZ = 'CZ',
  DK = 'DK',
  EE = 'EE',
  FI = 'FI',
  FR = 'FR',
  DE = 'DE',
  GR = 'GR',
  HU = 'HU',
  IS = 'IS',
  IT = 'IT',
  LI = 'LI',
  LV = 'LV',
  LT = 'LT',
  LU = 'LU',
  MC = 'MC',
  MT = 'MT',
  NL = 'NL',
  NO = 'NO',
  PL = 'PL',
  PT = 'PT',
  RO = 'RO',
  SK = 'SK',
  SI = 'SI',
  SM = 'SM',
  ES = 'ES',
  SE = 'SE',
}

/** Payment Type */
export enum PaymentDestinationType {
  ACCOUNT = 'ACCOUNT',
  TREASURY = 'TREASURY',
  BENEFICIARY = 'BENEFICIARY',
  EMPLOYEE = 'EMPLOYEE',
  IBAN = 'IBAN',
  SCAN = 'SCAN',
}

/**
 * Type of payment indicating the direction and nature of the payment.
 */
export enum PaymentType {
  /** Payment received into an account */
  INBOUND = 'INBOUND',
  /** Payment sent from an account */
  OUTBOUND = 'OUTBOUND',
  /** Return of a previous payment */
  RETURN = 'RETURN',
}

/**
 * Status of a payment throughout its lifecycle.
 * Includes both transient and final states.
 */
export enum PaymentStatus {
  /** Initial state when payment is first created in our system */
  CREATED = 'CREATED',
  /** Transaction is being processed by the financial service provider */
  PROCESSING = 'PROCESSING',
  /** Transaction completed successfully */
  SUCCEEDED = 'SUCCEEDED',
  /** Transaction failed to complete */
  FAILED = 'FAILED',
}

export enum BulkPaymentStatus {
  CREATED = 'CREATED',
  PROCESSING = 'PROCESSING',
  PARTIALLY_SUCCEEDED = 'PARTIALLY_SUCCEEDED',
  SUCCEEDED = 'SUCCEEDED',
  FAILED = 'FAILED',
}

export enum BulkPaymentType {
  PAYROLL = 'PAYROLL',
}

/**
 * Status of a payment authorisation throughout its lifecycle.
 * Includes both transient and final states.
 */
export enum AuthorisationStatus {
  /** Authorisation request has been created but not yet approved/rejected */
  PENDING = 'PENDING',
  /** Payment has been explicitly authorised by the user */
  AUTHORIZED = 'AUTHORIZED',
  /** Payment was rejected during the authorisation process */
  REJECTED = 'REJECTED',
  /** Payment doesn't require authorisation */
  NOT_REQUIRED = 'NOT_REQUIRED',
  /** Payment authorization failed */
  ERROR = 'ERROR',
  /** Authorization has been revoked by the user */
  REVOKED = 'REVOKED',
}

/**
 * Integration adapters used to connect with financial service providers. Each adapter implements specific API integration requirements.
 */
export enum FinancialServiceProviderAdapters {
  AIRWALLEX = 'AIRWALLEX',
  GOCARDLESS = 'GOCARDLESS',
  GOCARDLESS_DEMO_DATA = 'GOCARDLESS_DEMO_DATA',
  MODULR = 'MODULR',
  STRIPE = 'STRIPE',
  YAPILY = 'YAPILY',
  WEALTHKERNEL = 'WEALTHKERNEL',
  XERO = 'XERO', // Accounts imported from Xero (e.g., Revolut connected via Xero bank feeds)
}

export enum BaaSProviders {
  MODULR = 'MODULR',
}

export enum EvidenceType {
  INVOICE = 'invoice',
  PAYROLL = 'payroll',
}

export enum InvoicePaidByType {
  SYSTEM = 'SYSTEM',
  USER = 'USER',
  PAYMENT = 'PAYMENT',
}

export enum EvidenceSource {
  EMAIL_SCAN = 'EMAIL_SCAN',
  EMAIL_FORWARD = 'EMAIL_FORWARD',
  UPLOAD = 'UPLOAD',
  PHOTO = 'PHOTO',
}

export enum EvidenceStatus {
  PENDING = 'PENDING',
  PROCESSING = 'PROCESSING',
  PROCESSED = 'PROCESSED',
  IS_PAID_PROCESSED = 'IS_PAID_PROCESSED',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
}

/**
 * Represents the synchronization state of an entity with external data sources.
 * Used to track the current state of data synchronization processes.
 */
export enum SyncStatus {
  /** Initial state. Entity is ready for sync or scheduled for next sync attempt */
  PENDING = 'PENDING',
  /** Sync is actively running. Prevents concurrent sync attempts */
  IN_PROGRESS = 'IN_PROGRESS',
  /** Last sync completed successfully. Data is up to date */
  COMPLETE = 'COMPLETE',
  /** Last sync attempt failed. Check syncError for details and nextSyncAt for retry timing */
  FAILED = 'FAILED',
}

/**
 * Define valid syncable entity types.
 */
export enum SyncableEntityType {
  ACCOUNT = 'ACCOUNT',
  COMPANY = 'COMPANY',
  INVOICE_EVIDENCE = 'INVOICE_EVIDENCE',
  VENDOR = 'VENDOR',
  TRANSACTION = 'TRANSACTION',
}

/**
 * Represents the type of entity in an external system (e.g., Xero, QuickBooks).
 * Used to categorize external entities mapped to Seapoint entities.
 */
export enum ExternalEntityType {
  /** A bank account in the external system */
  ACCOUNT = 'ACCOUNT',
  /** A contact/vendor/supplier in the external system */
  CONTACT = 'CONTACT',
  /** An invoice in the external system */
  INVOICE = 'INVOICE',
  /** A transaction/payment in the external system */
  TRANSACTION = 'TRANSACTION',

  // Stripe-specific entity types
  /** Stripe connected account (account details with external bank accounts) */
  STRIPE_ACCOUNT = 'STRIPE_ACCOUNT',
  /** Stripe account balance */
  STRIPE_BALANCE = 'STRIPE_BALANCE',
  /** Stripe charge (payment received) */
  STRIPE_CHARGE = 'STRIPE_CHARGE',
  /** Stripe balance transaction */
  STRIPE_BALANCE_TRANSACTION = 'STRIPE_BALANCE_TRANSACTION',
  /** Stripe payout (transfer to bank account) */
  STRIPE_PAYOUT = 'STRIPE_PAYOUT',
  /** Stripe fee (processing fee or tax) */
  STRIPE_FEE = 'STRIPE_FEE',

  // Airwallex-specific entity types
  /** Airwallex global account */
  AIRWALLEX_ACCOUNT = 'AIRWALLEX_ACCOUNT',
  /** Airwallex financial transaction (canonical source for all money movement) */
  AIRWALLEX_FINANCIAL_TRANSACTION = 'AIRWALLEX_FINANCIAL_TRANSACTION',
  /** Airwallex payment (outbound payment to beneficiary) */
  AIRWALLEX_PAYMENT = 'AIRWALLEX_PAYMENT',
  /** Airwallex payout (payout to beneficiary) */
  AIRWALLEX_PAYOUT = 'AIRWALLEX_PAYOUT',
  /** Stripe customer (payer/buyer synced as vendor) */
  STRIPE_CUSTOMER = 'STRIPE_CUSTOMER',
}

/**
 * Represents the processing status of a transaction. Used to track the
 * lifecycle of financial transactions.
 */
export enum TransactionStatus {
  /** Transaction has been confirmed and settled. */
  BOOKED = 'BOOKED',
  /**
   * Transaction is in process but not yet settled. May affect available
   * balance but not booked balance.
   */
  PENDING = 'PENDING',
  /**
   * Transaction has been failed by the financial service provider
   * and will not be settled.
   * @see https://modulr.readme.io/docs/payment-statuses#error-statuses
   */
  FAILED = 'FAILED',
  /**
   * Transaction has been cancelled and will not be processed.
   */
  CANCELLED = 'CANCELLED',
  /**
   * Transaction (card authorisation) has been fully reversed before settlement.
   * Reserved funds have been released back to available balance.
   */
  FULL_REVERSAL = 'FULL_REVERSAL',
}

/** Categorizes the type of financial transaction. */
export enum TransactionType {
  /** Payment made using a card */
  CARD_PAYMENT = 'CARD_PAYMENT',
  /** Refund of a previous card payment */
  CARD_REFUND = 'CARD_REFUND',
  /** Disputed card transaction resulting in funds being returned */
  CARD_CHARGEBACK = 'CARD_CHARGEBACK',

  /** Payment received via payment processor (e.g., Stripe, PayPal) */
  PAYMENT = 'PAYMENT',
  /** Refund of a previous payment */
  PAYMENT_REFUND = 'PAYMENT_REFUND',
  /** Processing fee charged by payment processor (e.g., Stripe fees) */
  PAYMENT_FEE = 'PAYMENT_FEE',
  /** Tax deducted from payment (e.g., VAT, sales tax) */
  TAX = 'TAX',
  /** Payout/transfer from payment processor to bank account (e.g., Stripe payout) */
  PAYOUT = 'PAYOUT',

  /** Currency exchange transaction */
  EXCHANGE = 'EXCHANGE',
  /** Fee charged for currency conversion/FX */
  EXCHANGE_FEE = 'EXCHANGE_FEE',

  /** Direct Debit */
  DIRECT_DEBIT = 'DIRECT_DEBIT',

  /** Bank transfer between accounts */
  TRANSFER = 'TRANSFER',
  /** Refund of a previous transfer */
  REFUND = 'REFUND',

  /** Fee transaction */
  FEE = 'FEE',

  /** Miscellaneous transaction type. Used when transaction doesn't fit other categories. */
  OTHER = 'OTHER',
}

export enum RefreshDataEventType {
  INCREMENTAL = 'INCREMENTAL',
  FULL = 'FULL',
}

export enum RefreshDataDeduplicationKeys {
  INCREMENTAL_SYNC_AND_AI_ENRICHMENT_PIPELINE = 'INCREMENTAL_SYNC_AND_AI_ENRICHMENT_PIPELINE',
  ACCOUNT_BALANCE_CHANGE = 'ACCOUNT_BALANCE_CHANGE',
  MODULR_TRANSACTION_CREATED = 'MODULR_TRANSACTION_CREATED',
  DUPLICATE_TRANSACTION_REMOVAL = 'DUPLICATE_TRANSACTION_REMOVAL',
  TREASURY_TRANSACTION_SYNC = 'TREASURY_TRANSACTION_SYNC',
  CARD_TRANSACTION_UPDATED = 'CARD_TRANSACTION_UPDATED',
}

/**
 * Document types enum
 */
export enum DocumentType {
  MODULR_ONBOARDING = 'MODULR_ONBOARDING',
  INVOICE = 'INVOICE',
  PAYROLL_EVIDENCE = 'PAYROLL_EVIDENCE',
}

export enum EvidenceDeletedByType {
  USER = 'USER',
  SYSTEM = 'SYSTEM',
  ADMIN = 'ADMIN',
}

export enum EvidenceDeletionReason {
  EXCLUDED_SENDER = 'EXCLUDED_SENDER',
  LOW_CONFIDENCE_SCORE = 'LOW_CONFIDENCE_SCORE',
  CLEARED_EMAIL_SCAN_EVIDENCE = 'CLEARED_EMAIL_SCAN_EVIDENCE',
  MARKED_AS_NOT_INVOICE = 'MARKED_AS_NOT_INVOICE',
  DUPLICATE = 'DUPLICATE',
}

/**
 * Enum of supported TPP adapters
 */
export enum TPPAdapters {
  TELL_MONEY_EU = 'TELL_MONEY_EU',
  TELL_MONEY_UK = 'TELL_MONEY_UK',
}

/**
 * A list of types is useful for filtering and searching audit logs
 * Feel free to add more as needed
 */
export enum AuditLogEvent {
  ACCOUNT_STATE_CHANGE = 'ACCOUNT_STATE_CHANGE',
  ACCOUNT_AND_TRANSACTION_SYNC = 'ACCOUNT_AND_TRANSACTION_SYNC',
  ACCOUNT_BALANCE_CHANGE = 'ACCOUNT_BALANCE_CHANGE',
  ACCOUNT_MAPPINGS_UPDATED = 'ACCOUNT_MAPPINGS_UPDATED',
  AI_PIPELINE = 'AI_PIPELINE',
  PAYMENT_AUTHORISATION = 'PAYMENT_AUTHORISATION',
  PAYMENT_CALLBACK = 'PAYMENT_CALLBACK',
  PAYMENT_STATUS_CHANGE = 'PAYMENT_STATUS_CHANGE',
  PAYMENT_EXTERNAL_LINKED = 'PAYMENT_EXTERNAL_LINKED',
  PAYMENT_ALERT_SENT = 'PAYMENT_ALERT_SENT',
  /** Payment has been submitted to the bank after user authorization */
  PAYMENT_SUBMITTED = 'PAYMENT_SUBMITTED',
  /** Bank has confirmed the payment (via webhook) */
  PAYMENT_BANK_CONFIRMED = 'PAYMENT_BANK_CONFIRMED',
  // Invoice related events
  INVOICE_PAID_CHANGED = 'INVOICE_PAID_CHANGED',
  INVOICE_READER = 'INVOICE_READER',
  INVOICE_SYNCED_TO_ACCOUNTING = 'INVOICE_SYNCED_TO_ACCOUNTING',
  INVOICE_SYNC_STATE_CHANGE = 'INVOICE_SYNC_STATE_CHANGE',
  INVOICE_TRANSACTION_REMATCH = 'INVOICE_TRANSACTION_REMATCH',
  // OAuth related events
  OAUTH_AUTHORIZATION_FAILED = 'OAUTH_AUTHORIZATION_FAILED',
  OAUTH_AUTHORIZATION_SUCCEEDED = 'OAUTH_AUTHORIZATION_SUCCEEDED',
  OAUTH_CALLBACK_FAILED = 'OAUTH_CALLBACK_FAILED',
  OAUTH_CALLBACK_SUCCEEDED = 'OAUTH_CALLBACK_SUCCEEDED',
  OAUTH_CONNECTION_FAILED = 'OAUTH_CONNECTION_FAILED',
  OAUTH_CONNECTION_SUCCEEDED = 'OAUTH_CONNECTION_SUCCEEDED',
  OAUTH_CONNECTION_REVOCATION_FAILED = 'OAUTH_CONNECTION_REVOCATION_FAILED',
  OAUTH_CONNECTION_REVOCATION_SUCCEEDED = 'OAUTH_CONNECTION_REVOCATION_SUCCEEDED',
  OAUTH_CONNECTION_CREDENTIALS_MISSING = 'OAUTH_CONNECTION_CREDENTIALS_MISSING',
  // Email invoice extraction
  EMAIL_INVOICE_EXTRACTOR = 'EMAIL_INVOICE_EXTRACTOR',
  EMAIL_INVOICE_PROCESSOR = 'EMAIL_INVOICE_PROCESSOR',
  // Beneficiary related events
  BENEFICIARY_UPDATE = 'BENEFICIARY_UPDATE',
  BENEFICIARY_DELETE = 'BENEFICIARY_DELETE',
  // Transaction related events
  TRANSACTION_DUPLICATE_REMOVED = 'TRANSACTION_DUPLICATE_REMOVED',
  // OTP related events
  OTP_SENT = 'OTP_SENT',
  OTP_VERIFIED = 'OTP_VERIFIED',
  OTP_FAILED = 'OTP_FAILED',
  // User profile events
  USER_PROFILE_UPDATED = 'USER_PROFILE_UPDATED',
}

/**
 * Takes a string and an enum type and returns the enum value.
 */
/* eslint-disable 
  @typescript-eslint/no-explicit-any, 
  @typescript-eslint/no-unsafe-member-access, 
  @typescript-eslint/no-unsafe-assignment, 
  @typescript-eslint/no-unsafe-return 
*/
export const StringToEnum = (str: string, enumType: any) => {
  const enumValue = enumType[str as keyof typeof enumType];
  if (enumValue === undefined) {
    throw new Error(`Invalid enum value: ${str}`);
  }
  return enumValue;
  /* eslint-enable */
};

/**
 * This enum serves as the single source of truth for all supported providers
 * across different domains (email, banking, accounting, payments).
 */
export enum OAuthProvider {
  GMAIL = 'gmail',
  DUMMY = 'dummy',
  XERO = 'xero',
  AIRWALLEX = 'airwallex',
  STRIPE = 'stripe',
}

/**
 * OTP (One-Time Password) providers for SMS/voice verification.
 * Used by the OTP adapter factory to select the appropriate implementation.
 */
export enum OTPProvider {
  TWILIO = 'twilio',
}

export enum ConnectionStatus {
  CONNECTED = 'connected',
  DISCONNECTED = 'disconnected',
  ERROR = 'error',
  PENDING = 'pending',
}

export enum OpenBankingConnectionStatus {
  CONNECTED = 'connected',
  DISCONNECTED = 'disconnected',
}

export enum FeatureStatus {
  ENABLED = 'enabled',
  DISABLED = 'disabled',
  COMING_SOON = 'coming_soon',
}

export enum ConnectionFeatureType {
  GMAIL_SEARCH = 'gmail_search',
  GMAIL_USER_PROFILE = 'gmail_user_profile',
  XERO_SYNC_SEAPOINT_BANK_FEED = 'xero_sync_seapoint_bank_feed',
  XERO_SYNC_INVOICES = 'xero_sync_invoices',
  XERO_STREAMLINE_RECONCILIATION = 'xero_streamline_reconciliation',
}

export enum ConnectionShareScope {
  USER = 'user',
  ORGANIZATION = 'organization',
}
/**
 * Enum for employee status
 */
export enum EmployeeStatus {
  ACTIVE = 'Active',
  INACTIVE = 'Inactive',
}

export enum EmployeeDeletedByType {
  USER = 'USER',
  ADMIN = 'ADMIN',
}

/**
 * Enum for payroll status
 */
export enum PayrollStatus {
  IN_DRAFT = 'IN_DRAFT',
  WAITING_FOR_APPROVAL = 'WAITING_FOR_APPROVAL',
  APPROVED = 'APPROVED',
  PARTIALLY_SUCCEEDED = 'PARTIALLY_SUCCEEDED',
  SUCCEEDED = 'SUCCEEDED',
  FAILED = 'FAILED',
}

export enum GmailExtractionTimeRange {
  ONE_WEEK = '1 Week',
  ONE_MONTH = '1 Month',
  THREE_MONTHS = '3 Months',
  SIX_MONTHS = '6 Months',
  TWELVE_MONTHS = '12 Months',
}

// VoP CoP
export enum PayeeCheckType {
  VERIFY_PAYEE = 'VOP',
  CONFIRM_PAYEE = 'COP',
}

// VoP check response code
export enum VerifyPayeeCheckResponseCode {
  /**
   * The details provided matched the payee bank details.
   * You can proceed with the payment.
   * Remember, you should still exercise caution when sending payments. The service is an account name checking service and does not protect against all types of fraud.
   */
  MATCHED = 'MATCHED',

  /**
   * The account details provided do not match the payee bank details.
   * The account details should be checked carefully, you are advised not to proceed with the payment.
   * Authorising the payment could result in transferring the funds to a payment account not held by your intended recipient.
   * You should edit the details if possible, and submit another account verification check request before proceeding.
   */
  NO_MATCH = 'NO_MATCH',

  /**
   * The name provided is similar to the payee account name. The actual name is provided in matchedName.
   * The account details should be checked carefully.
   * If you decide to continue with the payment you should update the details before proceeding.
   */
  CLOSE_MATCH = 'CLOSE_MATCH',

  /**
   * The verification check is not possible.
   * You should check the account details carefully and proceed only if you are satisfied that they are correct.
   */
  CHECK_NOT_POSSIBLE = 'CHECK_NOT_POSSIBLE',

  /**
   * The request was malformed, missing or non-compliant JSON body or URL parameters.
   * You should check the account details carefully and proceed only if you are satisfied that they are correct.
   */
  INVALID = 'INVALID',

  /**
   * There is a technical error when trying to process the request.
   * You should check the account details carefully and proceed only if you are satisfied that they are correct.
   */
  GENERAL = 'GENERAL',
  /**
   * This code is generated internally by Seapoint when the payee check is not possible to be verified.
   */
  INTERNAL_ERROR = 'INTERNAL_ERROR',
}

// CoP check response code
export enum ConfirmationOfPayeeCheckResponseCode {
  /**
   * Account name and account type match the name and accountType provided.
   * You can proceed with the creation of a beneficiary or payment.
   * Note: Exercise caution when sending payments. The service is an account name checking service and does not protect against all types of fraud.
   */
  MATCHED = 'MATCHED',

  /**
   * Account name and account type do not match the name and accountType provided.
   * The account details should be checked carefully, and you are advised not to proceed with the creation of the beneficiary or payment.
   * Edit the details if possible, and submit another account name check request before proceeding.
   */
  NOT_MATCHED = 'NOT_MATCHED',

  /**
   * The name provided is similar to the account name. The actual account name is provided in result.name.
   * The account details should be checked carefully. If you decide to continue with the creation of the beneficiary/payment
   * you should edit the details if necessary before proceeding.
   */
  CLOSE_MATCH = 'CLOSE_MATCH',

  /**
   * The account name matches the name provided but the account is a business account, not a personal account.
   * The account details should be checked carefully before proceeding.
   */
  BUSINESS_ACCOUNT_NAME_MATCHED = 'BUSINESS_ACCOUNT_NAME_MATCHED',

  /**
   * The account name matches the name provided but the account is a personal account, not a business account.
   * The account details should be checked carefully before proceeding.
   */
  PERSONAL_ACCOUNT_NAME_MATCHED = 'PERSONAL_ACCOUNT_NAME_MATCHED',

  /**
   * The name provided is similar to the account name and the account is a business account, not a personal account.
   * The actual account name is provided in result.name.
   * The account details should be checked carefully. If you decide to continue with the creation of the beneficiary/payment
   * you should edit the details if necessary before proceeding.
   */
  BUSINESS_ACCOUNT_CLOSE_MATCH = 'BUSINESS_ACCOUNT_CLOSE_MATCH',

  /**
   * The name provided is similar to the account name and the account is a personal account, not a business account.
   * The actual account name is provided in result.name.
   * The account details should be checked carefully. If you decide to continue with the creation of the beneficiary/payment
   * you should edit the details if necessary before proceeding.
   */
  PERSONAL_ACCOUNT_CLOSE_MATCH = 'PERSONAL_ACCOUNT_CLOSE_MATCH',

  /**
   * The account does not exist.
   * You should not proceed. The account details should be checked carefully.
   * Edit the details if possible and submit another account name check request before creating a beneficiary or payment.
   */
  ACCOUNT_DOES_NOT_EXIST = 'ACCOUNT_DOES_NOT_EXIST',

  /**
   * The secondaryAccountId is not valid.
   * The account details should be checked carefully. The account could not be identified using the secondary account reference.
   * Edit the details if possible and submit another account name check request before proceeding.
   */
  SECONDARY_ACCOUNT_ID_NOT_FOUND = 'SECONDARY_ACCOUNT_ID_NOT_FOUND',

  /**
   * The account does not support account name check requests.
   * You should check the account details carefully and proceed only if you are satisfied that they are correct.
   */
  ACCOUNT_NOT_SUPPORTED = 'ACCOUNT_NOT_SUPPORTED',

  /**
   * The account has been switched using the Current Account Switching Service.
   * You should not proceed with the creation of a beneficiary or payment using these account details.
   * Obtain the new account details if possible, edit the details and submit another account name check request before creating a beneficiary or payment.
   */
  ACCOUNT_SWITCHED = 'ACCOUNT_SWITCHED',

  /**
   * The account name check cannot be completed for the account number and sort code provided.
   * You should check the account details carefully and proceed only if you are satisfied that they are correct.
   */
  WRONG_PARTICIPANT = 'WRONG_PARTICIPANT',

  /**
   * The bank did not respond to the account name check request.
   * It is recommended you try the check again after some time.
   * If you do decide to continue with this result you should check the account details carefully and proceed only if you are satisfied that they are correct.
   */
  NO_RESPONSE = 'NO_RESPONSE',

  /**
   * The account name check could not be completed as the bank does not accept account name check requests.
   * You should check the account details carefully and proceed only if you are satisfied that they are correct.
   */
  NOT_ENROLLED = 'NOT_ENROLLED',

  OPTED_OUT = 'OPTED_OUT',

  /**
   * This code is generated internally by Seapoint when the payee check is not possible to be verified.
   */
  INTERNAL_ERROR = 'INTERNAL_ERROR',
}

export enum InvestmentWithdrawalStatus {
  QUEUED = 'Queued', // Internal status, not used by WK
  PENDING = 'Pending',
  ACTIVE = 'Active',
  SETTLED = 'Settled',
  CANCELLING = 'Cancelling',
  CANCELLED = 'Cancelled',
  REJECTED = 'Rejected',
}

export enum InvestmentChargeStatus {
  REQUESTED = 'Requested',
  BOOKED = 'Booked',
  REJECTED = 'Rejected',
  CANCELLED = 'Cancelled',
}

/**
 * Method used to match Seapoint account to external (Xero) bank account
 */
export enum AccountMatchMethod {
  /** Matched by UK SCAN format (sort code + account number) */
  SCAN = 'scan',
  /** Matched by account number (including IBAN) */
  ACCOUNT_NUMBER = 'accountNumber',
  /** Matched by account name */
  NAME = 'name',
  /** Matched by Xero account code */
  CODE = 'code',
}

/**
 * Source/origin of an account mapping between Seapoint and external system
 */
export enum AccountMappingSource {
  /** Manually mapped by user via UI */
  USER_MAPPED = 'user-mapped',
  /** Automatically mapped during account fetch/setup */
  AUTO_MAPPED = 'auto-mapped',
  /** Automatically matched during invoice sync and persisted */
  AUTO_MATCHED = 'auto-matched',
  /** Configured in invoice metadata */
  METADATA = 'metadata',
  /** Fallback to first available account (not persisted) */
  FALLBACK = 'fallback',
}

/**
 * Method used to create an invoice in external system (Xero)
 */
export enum InvoiceCreationMethod {
  /** Invoice was matched to existing invoice in Xero */
  MATCHED = 'matched',
  /** New invoice was created in Xero */
  CREATED = 'created',
}

/** Cards */
export enum CardFormat {
  VIRTUAL = 'VIRTUAL',
  PHYSICAL = 'PHYSICAL',
}

export enum CardStatus {
  CREATED = 'CREATED',
  ACTIVE = 'ACTIVE',
  BLOCKED = 'BLOCKED',
  CANCELLED = 'CANCELLED',
  EXPIRED = 'EXPIRED',
  SUSPENDED = 'SUSPENDED',
}

export enum CardActivityType {
  AUTHORISATION = 'AUTHORISATION',
  REVERSAL = 'REVERSAL',
  SETTLEMENT = 'SETTLEMENT',
  REFUND = 'REFUND',
}

export enum CardAuthorisationStatus {
  APPROVED = 'APPROVED',
  DECLINED = 'DECLINED',
  SETTLED = 'SETTLED',
  EXPIRED = 'EXPIRED',
}

/**
 * Allowed KBA (Knowledge-Based Authentication) question types for 3D Secure authentication.
 * Used when creating virtual cards that require security questions.
 * @see https://modulr.readme.io/reference/updatecardauthentication
 */
export enum KbaQuestionType {
  FIRST_PET_NAME = 'FIRST_PET_NAME',
  MATERNAL_GRANDMOTHER_MAIDEN_NAME = 'MATERNAL_GRANDMOTHER_MAIDEN_NAME',
  FAVOURITE_CHILDHOOD_FRIEND = 'FAVOURITE_CHILDHOOD_FRIEND',
  FIRST_CAR = 'FIRST_CAR',
  CITY_PARENTS_MET = 'CITY_PARENTS_MET',
}

/**
 * Types of AI quality signals captured when the system makes mistakes
 * or produces suboptimal results that need correction.
 */
export enum AIQualitySignalType {
  // User corrections - direct feedback from users overriding AI decisions
  USER_OVERRIDE_INVOICE_PAID_STATUS = 'USER_OVERRIDE_INVOICE_PAID_STATUS',
  USER_OVERRIDE_TRANSACTION_MATCH = 'USER_OVERRIDE_TRANSACTION_MATCH',
  USER_OVERRIDE_VENDOR_NAME = 'USER_OVERRIDE_VENDOR_NAME',
  USER_OVERRIDE_VENDOR_CATEGORY = 'USER_OVERRIDE_VENDOR_CATEGORY',
  USER_OVERRIDE_BENEFICIARY = 'USER_OVERRIDE_BENEFICIARY',
  USER_OVERRIDE_INVOICE_FIELD = 'USER_OVERRIDE_INVOICE_FIELD',

  // Judge disagreements - automated QA finding issues
  JUDGE_DISAGREES_INVOICE_MATCH = 'JUDGE_DISAGREES_INVOICE_MATCH',
  JUDGE_DISAGREES_VENDOR_NAME = 'JUDGE_DISAGREES_VENDOR_NAME',

  // Low confidence - AI self-reported uncertainty
  LOW_CONFIDENCE_INVOICE_OCR = 'LOW_CONFIDENCE_INVOICE_OCR',
  LOW_CONFIDENCE_TRANSACTION_MATCH = 'LOW_CONFIDENCE_TRANSACTION_MATCH',
  LOW_CONFIDENCE_VENDOR_CLUSTERING = 'LOW_CONFIDENCE_VENDOR_CLUSTERING',

  // External validation - contradictions from external systems
  EXTERNAL_SYSTEM_CONTRADICTS = 'EXTERNAL_SYSTEM_CONTRADICTS',
  PAYMENT_CONTRADICTS_AI = 'PAYMENT_CONTRADICTS_AI',

  // Re-processing - admin triggered corrections
  ADMIN_TRIGGERED_REPROCESS = 'ADMIN_TRIGGERED_REPROCESS',
}

/**
 * AI agents that make decisions which can be captured as quality signals.
 */
export enum AIAgentType {
  INVOICE_READER = 'INVOICE_READER',
  INVOICE_TRANSACTION_MATCHER = 'INVOICE_TRANSACTION_MATCHER',
  VENDOR_ENRICHMENT = 'VENDOR_ENRICHMENT',
  VENDOR_CATEGORY_MATCH = 'VENDOR_CATEGORY_MATCH',
  TRANSACTION_CLUSTERING = 'TRANSACTION_CLUSTERING',
  XERO_INVOICE_MATCHER = 'XERO_INVOICE_MATCHER',
  PAYROLL_EVIDENCE_ENRICHMENT = 'PAYROLL_EVIDENCE_ENRICHMENT',
}

/**
 * Source that triggered the AI quality signal.
 */
export enum AIQualitySignalSource {
  USER = 'USER',
  JUDGE = 'JUDGE',
  EXTERNAL_SYNC = 'EXTERNAL_SYNC',
  ADMIN = 'ADMIN',
  AI_LOW_CONFIDENCE = 'AI_LOW_CONFIDENCE',
}

/**
 * Types of events that can occur during a transaction lifecycle.
 * Transaction-centric - works for PIS, Modulr, Cards, and WealthKernel.
 */
export enum TransactionEventType {
  // Payment initiation
  PAYMENT_AUTHORIZATION_COMPLETED = 'PAYMENT_AUTHORIZATION_COMPLETED',
  PAYMENT_SUBMITTED = 'PAYMENT_SUBMITTED',

  // Processing
  PROCESSING_STARTED = 'PROCESSING_STARTED',

  // Completion
  TRANSACTION_SETTLED = 'TRANSACTION_SETTLED',
  TRANSACTION_FAILED = 'TRANSACTION_FAILED',
}

/**
 * Status of a transaction event step.
 */
export enum TransactionEventStatus {
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
}
